import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
  FunctionField,
  NumberField,
  RecordContextProvider,
  ReferenceManyField,
  TabbedForm,
  TabbedFormViewProps,
  TextField,
  useGetManyReference,
} from 'react-admin';
import { EditableDatagridConfigurable } from '@react-admin/ra-editable-datagrid';
import { ReactElement } from 'react';
import { useWatch } from 'react-hook-form';
import { LabelResource } from '../../../Entities/Label';
import { getLabelInputTypeName, LABEL_INPUT_TYPE } from '../../Labels/inputTypes';
import { LabelValueNameInfoLabel, LabelValueValueInfoLabel } from './LabelValueInfoLabel';
import LabelRowForm from './LabelRowForm';
import LabelValueRowForm from './LabelValueRowForm';

export default function CategoryLabelsTab(props: TabbedFormViewProps): ReactElement {
  const categoryId = useWatch({ name: 'id' }) as string | undefined;
  const { data: labels = [], isLoading: isLabelsLoading } = useGetManyReference<LabelResource>(
    'labels',
    {
      id: categoryId ?? '',
      target: 'category.id',
      pagination: { page: 1, perPage: 250 },
      sort: { field: 'attributes.display_order', order: 'ASC' },
    },
    {
      enabled: Boolean(categoryId),
    },
  );

  if (!categoryId) {
    return (
      <TabbedForm.Tab label="Labels" {...props}>
        <Alert severity="info">Save the category first to manage labels.</Alert>
      </TabbedForm.Tab>
    );
  }

  const listLabels = labels.filter(
    (label) => label.attributes.input_type === LABEL_INPUT_TYPE.LIST,
  );

  return (
    <TabbedForm.Tab label="Labels" {...props}>
      <ReferenceManyField
        reference="labels"
        target="category.id"
        perPage={250}
        pagination={false}
        sort={{ field: 'attributes.display_order', order: 'ASC' }}
      >
        <EditableDatagridConfigurable
          bulkActionButtons={false}
          createForm={<LabelRowForm categoryId={categoryId} />}
          editForm={<LabelRowForm categoryId={categoryId} />}
        >
          <TextField label="Name" source="attributes.name" />
          <FunctionField
            label="Input Type"
            render={(record) =>
              getLabelInputTypeName((record as LabelResource | undefined)?.attributes.input_type)
            }
          />
          <NumberField label="Display Order" source="attributes.display_order" />
          <TextField label="Unit" source="attributes.unit" />
          <TextField label="Direction" source="attributes.direction" />
        </EditableDatagridConfigurable>
      </ReferenceManyField>
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          List Choices
        </Typography>
        {isLabelsLoading ? (
          <CircularProgress size={24} />
        ) : listLabels.length === 0 ? (
          <Alert severity="info">Add a List label to manage its choices here.</Alert>
        ) : (
          listLabels.map((label) => (
            <Box key={label.id} mb={4}>
              <Typography variant="subtitle1" gutterBottom>
                {label.attributes.name || `Label ${label.id}`}
              </Typography>
              <RecordContextProvider value={label}>
                <ReferenceManyField
                  reference="label_values"
                  target="label.id"
                  perPage={250}
                  pagination={false}
                  sort={{ field: 'attributes.display_order', order: 'ASC' }}
                >
                  <EditableDatagridConfigurable
                    bulkActionButtons={false}
                    createForm={<LabelValueRowForm categoryId={categoryId} labelId={label.id} />}
                    editForm={<LabelValueRowForm categoryId={categoryId} labelId={label.id} />}
                  >
                    <TextField label={<LabelValueNameInfoLabel />} source="attributes.name" />
                    <TextField label={<LabelValueValueInfoLabel />} source="attributes.value" />
                    <NumberField label="Display Order" source="attributes.display_order" />
                  </EditableDatagridConfigurable>
                </ReferenceManyField>
              </RecordContextProvider>
            </Box>
          ))
        )}
      </Box>
    </TabbedForm.Tab>
  );
}
